/**
 * Labstep
 *
 * @module components/ProtocolCondition/SmartTableCollapsibleCard
 * @desc Component to show a collapsible smart table protocolCondition card
 */

import EntitySearch from 'labstep-web/components/Entity/Search';
import { BulkAction } from 'labstep-web/components/Entity/Search/BulkActions';
import type { EntitySearchContainerChildrenProps } from 'labstep-web/components/Entity/Search/Children/types';
import { EntityViewFormShowEditName } from 'labstep-web/components/EntityView/Form/ShowEdit/Name';
import { ProtocolConditionSmartTableModal } from 'labstep-web/components/ProtocolCondition/SmartTable/Modal';
import { ParamsStateContextProvider } from 'labstep-web/contexts/params-state';
import type { ParamsStateContextType } from 'labstep-web/contexts/params-state/types';
import { ActionComponent } from 'labstep-web/core/Action/Component';
import { CollapsibleCard } from 'labstep-web/core/Collapsible/Card';
import { Flex } from 'labstep-web/core/Flex';
import { Icon } from 'labstep-web/core/Icon';
import { ExportProgressProvider } from 'labstep-web/grid/Index/components/DataGridExportAsCSV/context';
import { ToolPanelProvider } from 'labstep-web/grid/Index/components/ToolPanel/context';
import { GridSmartTable as GridSmartTableProtocolCondition } from 'labstep-web/grid/SmartTableProtocolCondition';
import {
  getSystemFilterExperimentGuid,
  getSystemFilterProtocolGuid,
} from 'labstep-web/grid/SmartTableProtocolCondition/services/grid-smart-table-protocol-condition.service';
import { GridFilterService } from 'labstep-web/grid/services/grid-filter.service';
import { EntityView } from 'labstep-web/models/entity-view.model';
import { Experiment } from 'labstep-web/models/experiment.model';
import { ProtocolCondition } from 'labstep-web/models/protocol-condition.model';
import type React from 'react';
import type { ProtocolConditionSmartTableCollapsibleCardProps } from './types';

export const ProtocolConditionSmartTableCollapsibleCard: React.FC<
  ProtocolConditionSmartTableCollapsibleCardProps
> = ({
  entityView,
  experimentOrProtocol,
  initialExpandedState = true,
  group,
  expandComponent,
  disableHeader = false,
}) => {
  const globalParams: Record<
    string,
    string | number | Record<string, unknown>[]
  > = {
    count: 100,
    serializerGroups: 'smart_table,paginator',
  };

  const systemFilter =
    experimentOrProtocol instanceof Experiment
      ? getSystemFilterExperimentGuid(experimentOrProtocol.guid)
      : getSystemFilterProtocolGuid(experimentOrProtocol.guid);

  const paramsStateContext: ParamsStateContextType = {
    isGrid: true,
    name: 'smart_table',
    injectFields: {
      [experimentOrProtocol.entityName]: {
        id: experimentOrProtocol.id,
      },
    },
    globalParams,
    entityView,
    initialSearchParams:
      GridFilterService.injectSystemFilter(
        systemFilter,
        entityView.parameters,
      ) || {},
    loadedEntityView: new EntityView({
      ...entityView,
      parameters:
        GridFilterService.injectSystemFilter(
          systemFilter,
          entityView.parameters,
        ) || {},
    }),
  };

  return (
    <ParamsStateContextProvider value={paramsStateContext}>
      <ExportProgressProvider>
        <ToolPanelProvider>
          <EntitySearch
            entityName={ProtocolCondition.entityName}
            useAllPages
            hideFilters
            hideSearch
            bulkActions={[BulkAction.delete]}
            showEmptyStateOnSearchOnly
            usePostFilter
          >
            {({
              entities,
              readNextPage,
              status,
              read,
            }: EntitySearchContainerChildrenProps) => {
              return (
                <CollapsibleCard>
                  <CollapsibleCard.Item
                    key="protocol-condition-smart-table-card-item"
                    name={
                      disableHeader ? (
                        <Flex>
                          <Icon name={'random'} />
                          {entityView.name ? (
                            <span>{entityView.name}</span>
                          ) : (
                            'Conditions'
                          )}
                        </Flex>
                      ) : (
                        <EntityViewFormShowEditName
                          entityView={entityView}
                        />
                      )
                    }
                    disabled={disableHeader}
                    actions={[
                      expandComponent ? (
                        expandComponent
                      ) : (
                        <ProtocolConditionSmartTableModal
                          experimentOrProtocol={experimentOrProtocol}
                          entityView={entityView}
                          viewComponent={({ toggleModal }) => (
                            <ActionComponent
                              dataCy="components/ProtocolCondition/SmartTableCollapsibleCard:fullscreen"
                              type="icon"
                              icon="external alternate"
                              elementProps={{ color: 'blue' }}
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleModal();
                              }}
                            />
                          )}
                        />
                      ),
                    ]}
                    content={
                      <Flex
                        grow
                        height={551}
                        data-cy="components/ProtocolCondition/SmartTableCollapsibleCard"
                      >
                        <GridSmartTableProtocolCondition
                          experimentOrProtocol={experimentOrProtocol}
                          entityName={entityView.entity_name}
                          group={group}
                          entities={entities.filter(
                            (entity) => !entity.deleted_at,
                          )}
                          loadMore={readNextPage}
                          status={status}
                          entityView={entityView}
                          read={read}
                          fullscreen={false}
                          withEmptyState={
                            experimentOrProtocol instanceof Experiment
                          }
                        />
                      </Flex>
                    }
                    initialExpandedState={initialExpandedState}
                  />
                </CollapsibleCard>
              );
            }}
          </EntitySearch>
        </ToolPanelProvider>
      </ExportProgressProvider>
    </ParamsStateContextProvider>
  );
};
